import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  ButtonBase,
  Dialog,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

import ResponsiveSheet from '../shared/ResponsiveSheet';
import { TOKENS, fmtMoney, pointTier } from '../theme';

// Spec §6 — Points are READ-ONLY. No edit mode, no Manager toggle.
// Color tiers: $10-20 slate, $40-60 blue, $100+ amber.
export default function PointsDetail({ sessionData }) {
  const [openDenom, setOpenDenom] = useState(null);
  const [vipPhotoUrl, setVipPhotoUrl] = useState(null);

  const points = sessionData?.points || [];
  const total = points.reduce((s, p) => s + Number(p.points || 0), 0);

  // Denomination breakdown for the tappable chips
  const denoms = useMemo(() => {
    const m = {};
    points.forEach((p) => {
      const v = Number(p.points || 0);
      if (!m[v]) m[v] = [];
      m[v].push(p);
    });
    return Object.entries(m)
      .map(([v, entries]) => ({
        value: Number(v),
        count: entries.length,
        entries,
      }))
      .sort((a, b) => a.value - b.value);
  }, [points]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1.5 }}>
      {/* Hero summary */}
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.emerald200}`,
          borderRadius: 2,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: TOKENS.slate500,
            }}
          >
            Total Points Given
          </Typography>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 700,
              fontFamily: 'monospace',
              color: TOKENS.slate900,
            }}
          >
            {fmtMoney(total)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TOKENS.slate500, mt: 0.25 }}>
            {points.length} matches
          </Typography>
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: TOKENS.emerald50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <StarIcon sx={{ color: TOKENS.emerald700, fontSize: 24 }} />
        </Box>
      </Box>

      {/* Denomination chips */}
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.slate200}`,
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate500,
            mb: 1,
          }}
        >
          Breakdown by Amount · Tap to view
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {denoms.map((d) => {
            const t = pointTier(d.value);
            return (
              <ButtonBase
                key={d.value}
                onClick={() => setOpenDenom(d)}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  background: '#fff',
                  border: `1px solid ${TOKENS.slate200}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: t.color,
                  }}
                >
                  ${d.value}
                </Typography>
                <Typography sx={{ fontSize: 10, color: TOKENS.slate500 }}>
                  ×
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                  }}
                >
                  {d.count}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {/* Points list */}
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.slate200}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {points.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: TOKENS.slate400,
              fontSize: 13,
            }}
          >
            No points matches for this session.
          </Box>
        )}
        {points.map((p, i) => {
          const t = pointTier(p.points);
          return (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 1,
                borderBottom:
                  i < points.length - 1
                    ? `1px solid ${TOKENS.slate100}`
                    : 'none',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 40,
                  background: t.dot,
                  borderRadius: 0.5,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: TOKENS.slate900 }}
                  noWrap
                >
                  {p.customer}
                </Typography>
                <Typography sx={{ fontSize: 11, color: TOKENS.slate500 }}>
                  {p.time || ''}
                </Typography>
              </Box>
              {p.machine && (
                <Chip
                  size="small"
                  label={`Mach ${p.machine}`}
                  sx={{
                    height: 18,
                    fontSize: 9,
                    fontWeight: 700,
                    background: TOKENS.slate100,
                    color: TOKENS.slate700,
                  }}
                />
              )}
              {p.vipPhotoUrl && (
                <IconButton
                  size="small"
                  onClick={() => setVipPhotoUrl(p.vipPhotoUrl)}
                  title="View VIP match photo"
                  sx={{ color: TOKENS.emerald700, p: 0.5 }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: t.color,
                  width: 56,
                  textAlign: 'right',
                }}
              >
                ${p.points}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <ResponsiveSheet open={!!openDenom} onClose={() => setOpenDenom(null)}>
        {openDenom && (
          <Box>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${TOKENS.slate200}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: TOKENS.slate500,
                  }}
                >
                  Customers who received
                </Typography>
                <Typography
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: pointTier(openDenom.value).color,
                    }}
                  >
                    ${openDenom.value}
                  </span>
                  <span style={{ color: TOKENS.slate500 }}>
                    · {openDenom.count}{' '}
                    {openDenom.count === 1 ? 'match' : 'matches'}
                  </span>
                </Typography>
              </Box>
              <IconButton onClick={() => setOpenDenom(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {openDenom.entries.map((p, i) => (
                <Box
                  key={i}
                  sx={{
                    px: 2,
                    py: 1,
                    borderBottom: `1px solid ${TOKENS.slate100}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                      {p.customer}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: TOKENS.slate500 }}>
                      {p.time || ''}
                    </Typography>
                  </Box>
                  {p.machine && (
                    <Chip
                      size="small"
                      label={`Mach ${p.machine}`}
                      sx={{
                        height: 18,
                        fontSize: 9,
                        fontWeight: 700,
                        background: TOKENS.slate100,
                        color: TOKENS.slate700,
                      }}
                    />
                  )}
                  {p.vipPhotoUrl && (
                    <IconButton
                      size="small"
                      onClick={() => setVipPhotoUrl(p.vipPhotoUrl)}
                      title="View VIP match photo"
                      sx={{ color: TOKENS.emerald700, p: 0.5 }}
                    >
                      <PhotoCameraIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${TOKENS.slate200}`,
                background: TOKENS.slate50,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.slate700 }}
              >
                Subtotal
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: pointTier(openDenom.value).color,
                }}
              >
                {fmtMoney(openDenom.value * openDenom.count)}
              </Typography>
            </Box>
          </Box>
        )}
      </ResponsiveSheet>

      <Dialog
        open={Boolean(vipPhotoUrl)}
        onClose={() => setVipPhotoUrl(null)}
        maxWidth="md"
      >
        {vipPhotoUrl && (
          <img
            src={vipPhotoUrl}
            alt="VIP"
            style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }}
          />
        )}
      </Dialog>
    </Box>
  );
}
